import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Spin,
  Typography,
  notification,
  Table,
  Button,
  Progress,
  Card,
  Row,
  Col,
  Tabs,
  Input,
  App,
} from 'antd';
import { SyncOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import { PageLayout } from '../components/PageLayout';
import { useProductOperations } from '../hooks';
import dayjs from 'dayjs';
import { createApiUrl } from '../config/api';
import type {
  ApiResponse,
  IDailyProductStat,
  UserSyncState,
  ProductStatsData,
  PlanData,
  PlanResponse,
  PlanEditStates,
} from '../types/stats';
import { ProductStatsTable } from '../components/ProductStatsTable/ProductStatsTable';

const { Title, Paragraph, Text } = Typography;

export const Stats: React.FC = () => {
  const { currentUser, isLoading: isUserLoading } = useSelector((state: RootState) => state.user);
  const token = localStorage.getItem('token');

  // Use message hook instead of static methods
  const { message } = App.useApp();

  // Use the useProductOperations hook to get user's products
  const {
    products: userProducts,
    isLoading: isLoadingUserProducts,
    fetchProducts,
  } = useProductOperations(token || undefined, {
    showSuccessMessages: false,
    showErrorMessages: true,
  });

  // Updated state to handle all products stats
  const [allProductsStats, setAllProductsStats] = useState<ProductStatsData[]>([]);
  const [isLoadingProductStats, setIsLoadingProductStats] = useState<boolean>(false);

  // Plan state management
  const [planData, setPlanData] = useState<PlanData>({});

  // Clean sync tracking - only track syncs initiated by current session
  const [currentSessionSyncId, setCurrentSessionSyncId] = useState<string | null>(null);
  const [syncProgress, setSyncProgress] = useState<UserSyncState | null>(null);
  const progressPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasShownCompletionNotificationRef = useRef<boolean>(false);

  // Plan edit state management
  const [planEditStates, setPlanEditStates] = useState<PlanEditStates>({});

  // Last sync info state
  const [lastSyncInfo, setLastSyncInfo] = useState<{
    lastSyncDate?: string;
    syncType?: string;
    status?: string;
  } | null>(null);
  const [lastSyncLoading, setLastSyncLoading] = useState<boolean>(false);

  // Row selection state for highlighting clicked rows
  const [selectedRows, setSelectedRows] = useState<Record<string, string | null>>({});

  // Function to load ALL plans for the current user at once
  const loadAllPlansForUser = useCallback(async () => {
    if (!currentUser?.wb_seller_sid) return;

    try {
      console.log('Loading all plans for user:', currentUser.wb_seller_sid);

      const response = await fetch(
        createApiUrl(`plans?wb_seller_sid=${currentUser.wb_seller_sid}`),
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (!response.ok) {
        // Silently fail if plans don't exist yet
        return;
      }

      const data = (await response.json()) as {
        plans: Record<string, Record<string, Record<string, number>>>; // {productId: {month: {metrics}}}
      };

      console.log('Loaded all plans data:', data);

      if (data?.plans) {
        // Update plan data state for all products and months
        const newPlanData: PlanData = {};
        const newPlanEditStates: PlanEditStates = {};

        Object.entries(data.plans).forEach(([productIdStr, productPlans]) => {
          const productId = Number(productIdStr);
          newPlanData[productId] = {};
          newPlanEditStates[productId] = {};

          Object.entries(productPlans).forEach(([monthKey, metrics]) => {
            newPlanData[productId][monthKey] = metrics;
            newPlanEditStates[productId][monthKey] = {
              sum: { isEditing: false, value: metrics.sum || 0 },
              costPerAdvertisingOrder: {
                isEditing: false,
                value: metrics.costPerAdvertisingOrder || 0,
              },
              ordersCount: { isEditing: false, value: metrics.ordersCount || 0 },
              buyoutsCount: { isEditing: false, value: metrics.buyoutsCount || 0 },
              price: { isEditing: false, value: metrics.price || 0 },
            };
          });
        });

        setPlanData(newPlanData);
        setPlanEditStates(newPlanEditStates);
      }
    } catch (error) {
      console.error('Error loading all plans:', error);
    }
  }, [currentUser?.wb_seller_sid]);

  // Function to load plans for a specific product and month
  const loadPlansForProduct = useCallback(
    async (productId: number, monthKey: string) => {
      if (!currentUser?.wb_seller_sid) return;

      try {
        const response = await fetch(
          createApiUrl(
            `plans?wb_seller_sid=${currentUser.wb_seller_sid}&productId=${productId}&month=${monthKey}`
          ),
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );

        if (!response.ok) {
          // Silently fail if plans don't exist yet
          return;
        }

        const data = (await response.json()) as PlanResponse;

        // Update plan data state
        setPlanData((prev) => ({
          ...prev,
          [productId]: {
            ...prev[productId],
            [monthKey]: data.plans || {},
          },
        }));

        // Update plan edit states with loaded values
        setPlanEditStates((prev) => ({
          ...prev,
          [productId]: {
            ...prev[productId],
            [monthKey]: {
              sum: { isEditing: false, value: data.plans.sum || 0 },
              costPerAdvertisingOrder: {
                isEditing: false,
                value: data.plans.costPerAdvertisingOrder || 0,
              },
              ordersCount: { isEditing: false, value: data.plans.ordersCount || 0 },
              buyoutsCount: { isEditing: false, value: data.plans.buyoutsCount || 0 },
              price: { isEditing: false, value: data.plans.price || 0 },
            },
          },
        }));
      } catch (error) {
        console.error('Error loading plans:', error);
        // Silently fail - we don't want to show an error message if plan values don't exist yet
      }
    },
    [currentUser?.wb_seller_sid]
  );

  // Function to toggle edit mode for a specific metric, product, and month
  const toggleEditMode = useCallback((productId: number, monthKey: string, metricKey: string) => {
    console.log('toggleEditMode called:', { productId, monthKey, metricKey });
    setPlanEditStates((prev) => {
      const currentState = prev[productId]?.[monthKey]?.[metricKey];
      const isCurrentlyEditing = currentState?.isEditing || false;
      console.log('Current edit state:', { isCurrentlyEditing, value: currentState?.value });

      // If turning off edit mode and there's a value, save the data
      if (isCurrentlyEditing && currentState?.value) {
        console.log('Saving plan with value:', currentState.value);
        // Pass the value directly instead of reading from state later
        void savePlanToBackendWithValue(
          productId,
          monthKey,
          metricKey,
          currentState.value.toString()
        );
      }

      return {
        ...prev,
        [productId]: {
          ...prev[productId],
          [monthKey]: {
            ...prev[productId]?.[monthKey],
            [metricKey]: {
              ...currentState,
              isEditing: !isCurrentlyEditing,
            },
          },
        },
      };
    });
  }, []);

  // Function to update plan value
  const updatePlanValue = useCallback(
    (productId: number, monthKey: string, metricKey: string, value: string) => {
      setPlanEditStates((prev) => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          [monthKey]: {
            ...prev[productId]?.[monthKey],
            [metricKey]: {
              ...prev[productId]?.[monthKey]?.[metricKey],
              value,
            },
          },
        },
      }));
    },
    []
  );

  // Function to save plan data to the backend
  const savePlanToBackend = useCallback(
    async (productId: number, monthKey: string, metricKey: string) => {
      if (!currentUser?.wb_seller_sid) return;

      const value = planEditStates[productId]?.[monthKey]?.[metricKey]?.value;
      if (!value) return;

      // Show loading indicator
      void message.loading({ content: 'Сохранение...', key: 'planSave' });

      try {
        // Make API call to save the plan value
        const response = await fetch(createApiUrl('plans'), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            productId,
            metricType: metricKey,
            planValue: value,
            month: monthKey,
            wb_seller_sid: currentUser.wb_seller_sid,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to save plan data');
        }

        // Update plan data state
        setPlanData((prev) => ({
          ...prev,
          [productId]: {
            ...prev[productId],
            [monthKey]: {
              ...prev[productId]?.[monthKey],
              [metricKey]: Number(value),
            },
          },
        }));

        // Show success message
        void message.success({ content: 'План сохранен', key: 'planSave', duration: 2 });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        void message.error({ content: 'Не удалось сохранить план', key: 'planSave', duration: 2 });
        console.error('Error saving plan:', errorMessage);
      }
    },
    [currentUser?.wb_seller_sid, planEditStates]
  );

  // Function to save plan data to the backend with a specific value
  const savePlanToBackendWithValue = useCallback(
    async (productId: number, monthKey: string, metricKey: string, value: string) => {
      console.log('savePlanToBackendWithValue called:', { productId, monthKey, metricKey, value });
      if (!currentUser?.wb_seller_sid) {
        console.log('No wb_seller_sid, returning early');
        return;
      }

      // Convert value to number for the backend
      const numericValue = Number(value);
      if (isNaN(numericValue)) {
        void message.error({
          content: 'Значение должно быть числом',
          key: 'planSave',
          duration: 2,
        });
        return;
      }

      // Show loading indicator
      void message.loading({ content: 'Сохранение...', key: 'planSave' });

      try {
        const requestBody = {
          productId,
          metricType: metricKey,
          planValue: numericValue, // Send as number
          month: monthKey,
          wb_seller_sid: currentUser.wb_seller_sid,
        };
        console.log('Making API request with body:', requestBody);

        // Make API call to save the plan value
        const response = await fetch(createApiUrl('plans'), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify(requestBody),
        });

        console.log('API response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.log('API error response:', errorText);
          throw new Error('Failed to save plan data');
        }

        const responseData = (await response.json()) as {
          _id: string;
          productId: number;
          wb_seller_sid: string;
          month: string;
          metrics: Record<string, string>;
        };
        console.log('API success response:', responseData);

        // Update plan data state
        setPlanData((prev) => ({
          ...prev,
          [productId]: {
            ...prev[productId],
            [monthKey]: {
              ...prev[productId]?.[monthKey],
              [metricKey]: Number(value),
            },
          },
        }));

        // Show success message
        void message.success({ content: 'План сохранен', key: 'planSave', duration: 2 });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        console.error('Error saving plan:', err);
        void message.error({ content: 'Не удалось сохранить план', key: 'planSave', duration: 2 });
      }
    },
    [currentUser?.wb_seller_sid]
  );

  // Effect to fetch user's products
  useEffect(() => {
    if (currentUser?.wb_api_token) {
      void fetchProducts();
    }
  }, [fetchProducts, currentUser?.wb_api_token]);

  // Function to fetch stats for all products
  const fetchAllProductsStats = useCallback(async () => {
    if (!currentUser?.wb_seller_sid || userProducts.length === 0) {
      return;
    }

    setIsLoadingProductStats(true);

    try {
      // Fetch stats for ALL products in a single bulk request
      const response = await fetch(createApiUrl('campaign/products/allstats'), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });

      if (!response.ok) {
        console.error(`Failed to fetch stats for all products: ${response.status}`);
        return;
      }

      const data = (await response.json()) as ApiResponse<
        Array<{
          productId: number;
          productName: string;
          campaignStats: IDailyProductStat[];
          nmReports: Array<{
            productId: number;
            productName: string;
            date: string;
            yearMonth: string;
            ordersSumRub?: number;
            buyoutsSumRub?: number;
            ordersCount?: number;
            buyoutsCount?: number;
            openCardCount?: number;
            addToCartCount?: number;
            updatedAt: Date;
          }>;
          combinedData: IDailyProductStat[];
        }>
      >;

      if (data && data.success && data.data) {
        const allProductsData: ProductStatsData[] = [];

        // Process each product's data
        data.data.forEach((productData) => {
          // Group stats by month for this product
          const monthsData: Record<string, IDailyProductStat[]> = {};

          productData.combinedData.forEach((stat) => {
            const monthKey = dayjs(stat.date).format('YYYY-MM');
            if (!monthsData[monthKey]) {
              monthsData[monthKey] = [];
            }
            monthsData[monthKey].push(stat);
          });

          allProductsData.push({
            productId: productData.productId,
            productName: productData.productName,
            monthsData,
          });
        });

        setAllProductsStats(allProductsData);
      }

      // Load ALL plans for the user at once (much more efficient!)
      await loadAllPlansForUser();
    } catch (error) {
      console.error('Error fetching all products stats:', error);
      notification.error({
        message: 'Ошибка загрузки статистики',
        description: 'Не удалось загрузить статистику для всех товаров.',
      });
    } finally {
      setIsLoadingProductStats(false);
    }
  }, [currentUser?.wb_seller_sid, userProducts, loadAllPlansForUser]);

  // Function to manually refresh stats data
  const refreshStatsData = useCallback(async () => {
    await fetchAllProductsStats();
  }, [fetchAllProductsStats]);

  // Function to fetch last sync info
  const fetchLastSyncInfo = useCallback(async () => {
    if (!token || !currentUser) return;
    setLastSyncLoading(true);
    console.log('Fetching last sync info...');
    try {
      const response = await fetch(createApiUrl('sync-history/latest'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('Sync history response status:', response.status);
      if (response.ok) {
        const data = (await response.json()) as ApiResponse<{
          latestSync?: { endTime?: string; syncType?: string; status?: string };
          lastStatsUpdate?: string;
          hasEverSynced?: boolean;
        }>;
        console.log('Sync history data:', data);
        if (data.success && data.data) {
          setLastSyncInfo({
            lastSyncDate: data.data.latestSync?.endTime || data.data.lastStatsUpdate,
            syncType: data.data.latestSync?.syncType,
            status: data.data.latestSync?.status,
          });
        } else {
          console.log('No sync data available');
          setLastSyncInfo(null);
        }
      } else {
        console.log('Failed to fetch sync history:', response.status);
        setLastSyncInfo(null);
      }
    } catch (error) {
      console.error('Error fetching last sync info:', error);
      setLastSyncInfo(null);
    } finally {
      setLastSyncLoading(false);
    }
  }, [token, currentUser]);

  // Effect to fetch ALL stats for all products when products are loaded
  useEffect(() => {
    if (userProducts.length > 0 && currentUser?.wb_seller_sid) {
      void fetchAllProductsStats();
    }
  }, [userProducts, currentUser?.wb_seller_sid, fetchAllProductsStats]);

  // Effect to fetch last sync info
  useEffect(() => {
    if (token && currentUser) {
      void fetchLastSyncInfo();
    }
  }, [token, currentUser, fetchLastSyncInfo]);

  // Function to stop progress polling
  const stopProgressPolling = useCallback(() => {
    if (progressPollingIntervalRef.current) {
      clearInterval(progressPollingIntervalRef.current);
      progressPollingIntervalRef.current = null;
    }
  }, []);

  // Function to fetch sync progress
  const fetchSyncProgress = useCallback(() => {
    if (!token) return;

    void (async () => {
      try {
        const response = await fetch(createApiUrl('campaign/sync-progress'), {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.status === 404) {
          // No sync in progress
          setSyncProgress(null);
          stopProgressPolling();
          return;
        }

        if (!response.ok) {
          throw new Error(`HTTP ошибка! статус: ${response.status}`);
        }

        const data = (await response.json()) as ApiResponse<UserSyncState>;

        if (data.success && data.data) {
          setSyncProgress(data.data);

          // Check if sync is completed/failed/cancelled
          const isCompleted =
            data.data.status === 'completed' ||
            data.data.status === 'failed' ||
            data.data.status === 'cancelled';

          if (isCompleted) {
            // IMMEDIATELY stop polling to prevent further calls
            stopProgressPolling();

            // Only show notifications for syncs started by current session and only once
            if (currentSessionSyncId && !hasShownCompletionNotificationRef.current) {
              hasShownCompletionNotificationRef.current = true;

              if (data.data.status === 'completed') {
                notification.success({
                  message: 'Синхронизация завершена',
                  description: `Статистика успешно обновлена. ${data.data.result?.elapsed || ''}`,
                });
                try {
                  await refreshStatsData();
                } catch (refreshError) {
                  console.error('❌ Error refreshing stats data:', refreshError);
                }
              } else if (data.data.status === 'failed') {
                notification.error({
                  message: 'Синхронизация не удалась',
                  description: data.data.error || 'Синхронизация завершилась с ошибкой.',
                });
              } else if (data.data.status === 'cancelled') {
                notification.warning({
                  message: 'Синхронизация отменена',
                  description: 'Синхронизация была отменена.',
                });
              }
            }
          }
        }
      } catch (error) {
        console.error('Ошибка получения прогресса синхронизации:', error);
      }
    })();
  }, [token, currentSessionSyncId, refreshStatsData, stopProgressPolling]);

  // Function to start progress polling
  const startProgressPolling = useCallback(() => {
    stopProgressPolling(); // Clear any existing interval
    const interval = setInterval(fetchSyncProgress, 3000);
    progressPollingIntervalRef.current = interval;
  }, [fetchSyncProgress, stopProgressPolling]);

  // Function to start sync
  const startSync = useCallback(() => {
    if (!token) {
      notification.error({
        message: 'Ошибка аутентификации',
        description: 'Пожалуйста, войдите в систему для синхронизации статистики.',
      });
      return;
    }

    void (async () => {
      try {
        const response = await fetch(createApiUrl('campaign/sync-adverts'), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = (await response.json()) as { message?: string };
          if (response.status === 409) {
            notification.warning({
              message: 'Синхронизация уже выполняется',
              description: errorData.message || 'Синхронизация уже запущена.',
            });
            return;
          }
          throw new Error(errorData.message || `HTTP ошибка! статус: ${response.status}`);
        }

        const data = (await response.json()) as { message?: string };

        // Generate unique sync ID for this session
        const syncId = `sync_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        setCurrentSessionSyncId(syncId);
        hasShownCompletionNotificationRef.current = false; // Reset notification flag for new sync

        notification.success({
          message: 'Синхронизация запущена',
          description: data.message || 'Синхронизация успешно запущена.',
        });

        // Start polling for progress
        startProgressPolling();
      } catch (error) {
        notification.error({
          message: 'Не удалось запустить синхронизацию',
          description: error instanceof Error ? error.message : 'Неизвестная ошибка.',
        });
      }
    })();
  }, [token, startProgressPolling]);

  // Function to close progress card
  const closeProgressCard = useCallback(() => {
    setCurrentSessionSyncId(null);
    setSyncProgress(null);
    hasShownCompletionNotificationRef.current = false;
    stopProgressPolling();
  }, [stopProgressPolling]);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      stopProgressPolling();
    };
  }, [stopProgressPolling]);

  // Check for existing RUNNING sync on component mount (don't show completed ones)
  useEffect(() => {
    if (token && currentUser) {
      void (async () => {
        try {
          const response = await fetch(createApiUrl('campaign/sync-progress'), {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (response.status === 404) {
            return; // No sync in progress
          }

          if (response.ok) {
            const data = (await response.json()) as ApiResponse<UserSyncState>;
            if (data.success && data.data && data.data.status === 'running') {
              // Only show progress for actively running syncs, and treat them as current session
              const syncId = `existing_sync_${Date.now()}`;
              setCurrentSessionSyncId(syncId);
              setSyncProgress(data.data);
              hasShownCompletionNotificationRef.current = false; // Reset for existing sync
              startProgressPolling();
            }
          }
        } catch (error) {
          console.error('Error checking existing sync:', error);
        }
      })();
    }
  }, [token, currentUser, startProgressPolling]);

  // Determine if we should show progress card
  const shouldShowProgressCard = Boolean(
    currentSessionSyncId &&
      syncProgress &&
      ['running', 'completed', 'failed', 'cancelled'].includes(syncProgress.status)
  );

  const isSyncing = Boolean(syncProgress?.status === 'running');

  // --- Data Transformation and Table Columns (functions now take monthStats as param) ---

  // Helper function to format remaining time in user-friendly format (hours and minutes)
  const formatRemainingTime = (totalSeconds: number): string => {
    if (totalSeconds <= 0) return '0мин';

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    if (hours > 0) {
      if (minutes > 0) {
        return `${hours}ч ${minutes}мин`;
      } else {
        return `${hours}ч`;
      }
    } else {
      return `${minutes}мин`;
    }
  };

  // Helper function to get status color for progress bars
  const getProgressStatus = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'exception';
      case 'running':
        return 'active';
      default:
        return 'normal';
    }
  };

  // Helper function to translate phase names to Russian
  const translatePhaseName = (phaseName: string) => {
    // Backend now returns Russian text directly, but keep some fallbacks for compatibility
    const translations: Record<string, string> = {
      'Sync Campaign Details': 'Синхронизация деталей кампаний',
      'Process Campaign Statistics': 'Обработка статистики кампаний',
      'Process NM Reports': 'Обработка отчетов по воронке продаж',
    };
    return translations[phaseName] || phaseName; // Return as-is if already in Russian
  };

  // Helper function to translate step names to Russian
  const translateStepName = (stepName: string) => {
    // Backend now returns Russian text directly, but keep some fallbacks for compatibility
    const translations: Record<string, string> = {
      'Fetch Campaign IDs': 'Получение ID кампаний',
      'Fetch Campaign Details': 'Получение деталей кампаний',
      'Save Campaign Details': 'Сохранение деталей кампаний',
      'Detect Status Changes': 'Обнаружение изменений статуса',
      'Select Campaigns for Processing': 'Выбор кампаний для обработки',
      'Generate Date Range Payloads': 'Генерация временных диапазонов',
      'Process Statistics Batches': 'Обработка пакетов статистики',
      'Save Statistics to Database': 'Сохранение статистики в базу данных',
      'Initialize NM Service': 'Инициализация сервиса воронки продаж',
      'Check Existing Ranges': 'Проверка существующих диапазонов',
      'Process NM Data': 'Обработка данных воронки продаж',
    };
    return translations[stepName] || stepName; // Return as-is if already in Russian
  };

  // Helper function to translate sync status to Russian
  const translateSyncStatus = (status: string) => {
    const translations: Record<string, string> = {
      idle: 'ожидание',
      running: 'выполняется',
      completed: 'завершено',
      failed: 'ошибка',
      cancelled: 'отменено',
      pending: 'ожидание',
    };
    return translations[status] || status;
  };

  // --- Render Logic ---
  if (isUserLoading) {
    return (
      <PageLayout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }
  if (!currentUser || !currentUser.wb_seller_sid) {
    return (
      <PageLayout>
        <Paragraph>
          Пожалуйста, войдите в систему и убедитесь, что ваш Wildberries Seller ID настроен для
          просмотра статистики.
        </Paragraph>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Статистика по товарам">
      <div style={{ marginBottom: '20px' }}>
        <Row gutter={16} align="middle">
          <Col>
            <Button
              type="primary"
              icon={<SyncOutlined spin={isSyncing} />}
              onClick={startSync}
              loading={isSyncing}
              disabled={!currentUser?.wb_api_token || !currentUser?.wb_seller_sid}
            >
              Обновить статистику
            </Button>
          </Col>
        </Row>

        {/* Last sync info display - only when data exists */}
        {!lastSyncLoading && lastSyncInfo && lastSyncInfo.lastSyncDate && (
          <Row style={{ marginTop: '8px' }}>
            <Col>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Последняя синхронизация:{' '}
                {dayjs(lastSyncInfo.lastSyncDate).format('DD.MM.YYYY HH:mm')}
                {lastSyncInfo.syncType === 'manual' && ' (ручная)'}
                {lastSyncInfo.syncType === 'scheduled' && ' (автоматическая)'}
                {lastSyncInfo.status === 'failed' && (
                  <span style={{ color: '#ff4d4f' }}> - неудачная</span>
                )}
                {lastSyncInfo.status === 'completed' && (
                  <span style={{ color: '#52c41a' }}> - успешная</span>
                )}
              </Text>
            </Col>
          </Row>
        )}

        {/* Progress tracking display */}
        {shouldShowProgressCard && (
          <Card
            style={{ marginBottom: '20px' }}
            extra={
              !isSyncing &&
              syncProgress?.status &&
              ['completed', 'failed', 'cancelled'].includes(syncProgress.status) ? (
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  onClick={closeProgressCard}
                  size="small"
                  title="Закрыть"
                />
              ) : null
            }
          >
            <Title level={5}>Прогресс синхронизации</Title>
            {syncProgress && (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <Text strong>Общий прогресс: </Text>
                  <Progress
                    percent={syncProgress.overallProgress}
                    status={getProgressStatus(syncProgress.status)}
                    showInfo={true}
                    format={(percent) => {
                      // Show estimated completion time if available
                      if (syncProgress.estimatedCompletion && syncProgress.status === 'running') {
                        const remainingMinutes = dayjs(syncProgress.estimatedCompletion).diff(
                          dayjs(),
                          'minute'
                        );
                        if (remainingMinutes > 0) {
                          // Convert minutes to seconds and use formatRemainingTime for consistent formatting
                          const remainingSeconds = remainingMinutes * 60;
                          return `${percent}% (~${formatRemainingTime(remainingSeconds)})`;
                        }
                      }
                      return `${percent}%`;
                    }}
                  />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Text type="secondary">Статус: {translateSyncStatus(syncProgress.status)}</Text>
                    {syncProgress.estimatedCompletion && syncProgress.status === 'running' && (
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Завершение: {dayjs(syncProgress.estimatedCompletion).format('HH:mm')}
                      </Text>
                    )}
                  </div>
                </div>

                {syncProgress.phases.map((phase) => (
                  <div key={phase.id} style={{ marginBottom: '16px' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Text strong>{translatePhaseName(phase.name)}</Text>
                    </div>
                    <Progress
                      percent={phase.progress}
                      status={getProgressStatus(phase.status)}
                      size="small"
                      showInfo={true}
                    />

                    {/* Show step details for active phase */}
                    {phase.status === 'running' && (
                      <div style={{ marginLeft: '16px', marginTop: '8px' }}>
                        {phase.steps.map((step) => (
                          <div key={step.id} style={{ marginBottom: '8px' }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {translateStepName(step.name)}: {Math.round(step.progress)}%
                              {step.status === 'running' && step.details && (
                                <span>
                                  {/* Campaign stats batch processing details */}
                                  {step.id === 'process-batches' &&
                                    Boolean(step.details.remainingTimeDisplay) && (
                                      <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
                                        {' '}
                                        → ~{String(step.details.remainingTimeDisplay)} осталось
                                      </span>
                                    )}
                                  {step.id === 'process-batches' &&
                                    Boolean(step.details.batchesProcessed) &&
                                    Boolean(step.details.totalBatches) && (
                                      <span>
                                        {' '}
                                        ({String(step.details.batchesProcessed)}/
                                        {String(step.details.totalBatches)} пакетов)
                                      </span>
                                    )}
                                  {step.id === 'process-batches' &&
                                    Boolean(step.details.avgBatchTimeMs) && (
                                      <span>
                                        {' '}
                                        [сред:{' '}
                                        {Math.round(Number(step.details.avgBatchTimeMs) / 1000)}
                                        с/пакет]
                                      </span>
                                    )}

                                  {/* NM reports processing details */}
                                  {step.id === 'process-data' &&
                                    Boolean(step.details.remainingTimeDisplay) &&
                                    Boolean(step.details.predictableCountdown) && (
                                      <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
                                        {' '}
                                        → {String(step.details.remainingTimeDisplay)} осталось
                                      </span>
                                    )}
                                  {step.id === 'process-data' &&
                                    Boolean(step.details.completedRequests) &&
                                    Boolean(step.details.totalRequests) && (
                                      <span>
                                        {' '}
                                        ({String(step.details.completedRequests)}/
                                        {String(step.details.totalRequests)} запросов)
                                      </span>
                                    )}

                                  {/* Generic processing details */}
                                  {typeof step.details.processedChunks === 'number' &&
                                    typeof step.details.totalChunks === 'number' && (
                                      <span>
                                        {' '}
                                        ({step.details.processedChunks}/{step.details.totalChunks}{' '}
                                        блоков)
                                      </span>
                                    )}
                                  {typeof step.details.batchesProcessed === 'number' &&
                                    typeof step.details.totalBatches === 'number' &&
                                    step.id !== 'process-batches' && (
                                      <span>
                                        {' '}
                                        ({step.details.batchesProcessed}/{step.details.totalBatches}{' '}
                                        пакетов)
                                      </span>
                                    )}
                                  {typeof step.details.estimatedRemainingSeconds === 'number' &&
                                    step.details.estimatedRemainingSeconds > 0 &&
                                    !step.details.remainingTimeDisplay && (
                                      <span>
                                        {' '}
                                        → ~
                                        {formatRemainingTime(
                                          Number(step.details.estimatedRemainingSeconds)
                                        )}{' '}
                                        осталось
                                      </span>
                                    )}
                                </span>
                              )}
                            </Text>
                          </div>
                        ))}
                      </div>
                    )}

                    {phase.status === 'failed' && phase.error && (
                      <Text
                        type="danger"
                        style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}
                      >
                        Ошибка: {phase.error}
                      </Text>
                    )}
                  </div>
                ))}

                {syncProgress.startTime && (
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Начато: {dayjs(syncProgress.startTime).format('DD.MM.YYYY HH:mm:ss')}
                  </Text>
                )}

                {syncProgress.status === 'completed' && syncProgress.result && (
                  <div
                    style={{
                      marginTop: '12px',
                      padding: '8px',
                      background: '#f6ffed',
                      border: '1px solid #b7eb8f',
                      borderRadius: '4px',
                    }}
                  >
                    <Text type="success" strong>
                      Синхронизация завершена успешно!
                    </Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Кампании: {syncProgress.result.details.total} обработано, Статистика:{' '}
                      {syncProgress.result.stats.successfulPayloads}/
                      {syncProgress.result.stats.totalPayloads} успешно, Время:{' '}
                      {syncProgress.result.elapsed}
                    </Text>
                  </div>
                )}
              </>
            )}
          </Card>
        )}
      </div>

      {/* All Products Stats Display */}
      <div style={{ marginTop: '20px' }}>
        {isLoadingProductStats ? (
          <Spin spinning={true} tip="Загрузка статистики для всех товаров...">
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '40px 0',
                minHeight: '100px',
              }}
            >
              {/* Content placeholder for spinner */}
            </div>
          </Spin>
        ) : allProductsStats.length > 0 ? (
          allProductsStats.map((productData) => {
            return (
              <ProductStatsTable
                key={productData.productId}
                productData={productData}
                planEditStates={planEditStates}
                toggleEditMode={toggleEditMode}
                updatePlanValue={updatePlanValue}
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
              />
            );
          })
        ) : userProducts.length === 0 && !isLoadingUserProducts ? (
          <Paragraph>
            Нет доступных товаров для отображения статистики. Добавьте товары для просмотра их
            статистики.
          </Paragraph>
        ) : userProducts.length > 0 ? (
          <Paragraph>
            Нет данных о статистике для ваших товаров. Запустите синхронизацию для загрузки данных.
          </Paragraph>
        ) : null}
      </div>
    </PageLayout>
  );
};
